import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { database } from "../../../firebaseConfig";
import { ref, onValue } from "firebase/database";
import ContractMaker from "../ContractMaker";
import "./CustomContractView.css";
import { Helmet } from "react-helmet-async";

/**
 * CustomContractView loads publicly shared contracts via a customId
 * and uses ContractMaker for display
 */
const CustomContractView = () => {
  const { customId } = useParams();
  const [contractData, setContractData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metaData, setMetaData] = useState({
    title: "Contract Details",
    description: "View the contract details and terms.",
    imageUrl: null
  });

  useEffect(() => {
    // Validate and extract userId and contractId from the customId
    if (!customId || !customId.includes("+")) {
      setError("Invalid contract link");
      setLoading(false);
      return;
    }

    const [userId, contractId] = customId.split("+");

    if (!userId || !contractId) {
      setError("Invalid contract link");
      setLoading(false);
      return;
    }

    // Try to load from public contracts
    const publicContractRef = ref(database, `/publicContracts/${customId}`);
    
    const unsubscribe = onValue(publicContractRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        
        setContractData({
          ...data,
          id: contractId
        });
        
        // Set meta data for social media preview
        const customerName = data.customerName || 'Contract';
        setMetaData({
          title: `Contract: ${customerName}`,
          description: `Contract between ${data.teamMember?.companyName || 'Company'} and ${customerName}`,
          imageUrl: data.teamMember?.profileImageUrl || null
        });
        
        setLoading(false);
      } else {
        setError("Contract not found or not public");
        setLoading(false);
      }
    }, (error) => {
      console.error("Error loading contract:", error);
      setError("Contract could not be loaded");
      setLoading(false);
    });

    return () => unsubscribe();
  }, [customId]);

  // Show loader, error, or empty state
  if (loading) {
    return (
      <>
        <Helmet>
          <title>Loading contract...</title>
          <meta name="description" content="Please wait, the contract is loading." />
        </Helmet>
        <div className="custom-contract-view">
          <div className="custom-contract-view__loading">
            <div className="custom-contract-view__spinner"></div>
            <p>Loading contract...</p>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Helmet>
          <title>Error loading contract</title>
          <meta name="description" content="The contract could not be loaded." />
        </Helmet>
        <div className="custom-contract-view">
          <div className="custom-contract-view__error">
            <span className="custom-contract-view__error-icon">⚠️</span>
            <h3>Error</h3>
            <p>{error}</p>
          </div>
        </div>
      </>
    );
  }

  if (!contractData) {
    return (
      <>
        <Helmet>
          <title>Contract not found</title>
          <meta name="description" content="The requested contract is not available." />
        </Helmet>
        <div className="custom-contract-view">
          <div className="custom-contract-view__empty">
            <span className="custom-contract-view__empty-icon">📄</span>
            <h3>Contract not found</h3>
            <p>The requested contract is not available.</p>
          </div>
        </div>
      </>
    );
  }

  // Use the ContractMaker component for display (read-only mode)
  return (
    <>
      <Helmet>
        <title>{metaData.title}</title>
        <meta name="description" content={metaData.description} />
        
        {/* Open Graph Meta Tags for Facebook, WhatsApp etc. */}
        <meta property="og:title" content={metaData.title} />
        <meta property="og:description" content={metaData.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:site_name" content="Bonus Club" />
        {metaData.imageUrl ? (
          <meta property="og:image" content={metaData.imageUrl} />
        ) : (
          <meta property="og:image" content={`${window.location.origin}/og-image.jpg`} />
        )}
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaData.title} />
        <meta name="twitter:description" content={metaData.description} />
        {metaData.imageUrl ? (
          <meta name="twitter:image" content={metaData.imageUrl} />
        ) : (
          <meta name="twitter:image" content={`${window.location.origin}/og-image.jpg`} />
        )}
        
        {/* Additional meta for better crawling */}
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={window.location.href} />
      </Helmet>
      
      <div className="custom-contract-view">
        <ContractMaker 
          userId={contractData.userId}
          initialContractData={contractData}
          isEditMode={false}
          onContractSaved={() => {}}
          onlyPreview={true}
        />
      </div>
    </>
  );
};

export default CustomContractView;

